import { Box, Text } from '@wix/design-system';
import classNames from 'classnames';
import { observer } from 'mobx-react';
import React, { createContext, useCallback, useContext, useMemo } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { keys } from '../../translationsKeys';
import { AppContextProvider } from '../AppLoader';
import { DialogContainerProps, DialogFormContainer } from '../DialogContainer';
import { DialogHeader } from '../DialogHeader/DialogHeader';
import { PoliciesContainer } from '../PoliciesContainer';
import {
  SocialAuth,
  SocialAuthIconsWithApple,
  SocialAuthIconsWithoutApple,
  SocialAuthWithoutApple,
} from '../SocialAuth/SocialAuth';
import { ThemedText } from '../ThemedComponents';
import s from './AuthPage.scss';

export const AuthPageContextProvider = createContext<{
  store?: AuthStore;
}>({});
interface AuthPageProps {
  container: DialogContainerProps;
}

const _AuthPage: React.FC<AuthPageProps> = observer((props) => {
  return (
    <DialogFormContainer
      {...props.container}
      formClasses={[s.form]}
      autoCenter={false} // Primary dialogs handle centering externally via dialogCenteredWrapper
    >
      {props.children}
    </DialogFormContainer>
  );
});

export const Header: React.FC<{
  dataHooks?: { [key: string]: string };
  subtitleValues?: { [key: string]: string };
  hidePresetSubtitle?: boolean;
  // When true, the `subtitleKey` subtitle renders at the default (small) WDS size
  // instead of the preset's enlarged `subtitleFontSize` — used on the Symphony
  // "you already have an account" screen per UX QA.
  smallSubtitle?: boolean;
}> = ({
  dataHooks,
  subtitleValues,
  hidePresetSubtitle = false,
  smallSubtitle = false,
  children,
}) => {
  const { store } = useContext(AuthPageContextProvider);
  const {
    rootStore: {
      displayStore: { preset, isVerticalLayout, isMobile },
    },
  } = useContext(AppContextProvider);
  const { t } = useTranslation();
  // Optional preset override for the subtitle typography (e.g. Symphony's 22px
  // per Figma). The size lives in CSS vars set inline here; the `.subtitleCustom`
  // class forces them onto the subtitle text and its WDS descendants (the nested
  // Box/Text otherwise reset the size). Only presets that set `subtitleFontSize`
  // are affected — others keep the default size.
  // `display: contents` so the wrapper adds no box (no layout change) but still
  // passes the inherited CSS vars down to the WDS subtitle (whose ThemedText
  // drops an inline `style`, so we can't set the vars on it directly).
  const subtitleStyle = preset?.subtitleFontSize
    ? ({
        display: 'contents',
        '--subtitle-font-size': preset.subtitleFontSize.fontSize,
        '--subtitle-line-height':
          preset.subtitleFontSize.lineHeight ?? 'normal',
        '--subtitle-mobile-font-size':
          preset.subtitleFontSize.mobileFontSize ??
          preset.subtitleFontSize.fontSize,
        '--subtitle-letter-spacing':
          preset.subtitleFontSize.letterSpacing ?? 'normal',
      } as React.CSSProperties)
    : undefined;
  const subtitleCustomClass = preset?.subtitleFontSize
    ? s.subtitleCustom
    : undefined;
  return (
    <DialogHeader dataHook={dataHooks?.header}>
      <DialogHeader.Title
        flow="authPage"
        className={classNames({
          [s.mobile]: isMobile,
        })}
        dataHook={dataHooks?.title}
      >
        {store?.titleKey &&
          t(store?.titleKey, { brand: t(preset?.nameKey ?? '') })}
      </DialogHeader.Title>
      {store?.presetSubtitleKey && !hidePresetSubtitle && (
        <div style={subtitleStyle}>
          <DialogHeader.SubTitleSmall
            className={classNames(s.subtitle, subtitleCustomClass, {
              [s.vertical]: isVerticalLayout,
            })}
            dataHook={dataHooks?.presetSubtitle}
          >
            <Box direction="vertical" align="center">
              <Trans i18nKey={store?.presetSubtitleKey} values={subtitleValues}>
                <ThemedText size="small" weight="thin" />
                <ThemedText size="small" weight="bold" />
              </Trans>
            </Box>
          </DialogHeader.SubTitleSmall>
        </div>
      )}
      {store?.subtitleKey && (
        <div style={smallSubtitle ? undefined : subtitleStyle}>
          <DialogHeader.SubTitleSmall
            className={classNames(s.subtitle, {
              [subtitleCustomClass ?? '']:
                !smallSubtitle && !!subtitleCustomClass,
              [s.vertical]: isVerticalLayout,
            })}
            dataHook={dataHooks?.subtitle}
            maxWidth="390px"
          >
            {t(store?.subtitleKey, { brand: t(preset?.nameKey ?? '') })}
          </DialogHeader.SubTitleSmall>
        </div>
      )}
      {children ?? <></>}
    </DialogHeader>
  );
};

export const Body: React.FC<{ dividerText?: string }> = observer(
  ({ children, dividerText }) => {
    const {
      rootStore: {
        displayStore: { authFormWidth, direction, isMobile, sideBySideForm },
        socialAuthStore,
      },
    } = useContext(AppContextProvider);
    return (
      <Box direction={direction} width="100%" maxWidth={authFormWidth}>
        {children}
        {!socialAuthStore.shouldHideSocialAuth && (
          <>
            <AuthDivider
              text={dividerText}
              className={classNames({ [s.horizontal]: !isMobile })}
            />
            <SocialAuth width={sideBySideForm.width} />
          </>
        )}
      </Box>
    );
  },
);

const MobileBody: React.FC<{ dividerText?: string }> = observer(
  ({ children }) => {
    const {
      rootStore: {
        socialAuthStore,
        displayStore: { sideBySideForm, isMobile },
      },
    } = useContext(AppContextProvider);
    const { t } = useTranslation();
    return (
      <>
        {children}
        {!socialAuthStore.shouldHideSocialAuth && (
          <Box
            gap={2}
            direction="horizontal"
            align="space-between"
            width={sideBySideForm.width}
            marginTop={3}
          >
            {!isMobile ||
              (!socialAuthStore.isUnsupportedAgentForSocialAuth && (
                <Box
                  direction="vertical"
                  verticalAlign="middle"
                  marginBottom={3}
                >
                  <Text>{t('emailStep.or.login.with')}</Text>
                </Box>
              ))}
            <SocialAuthIconsWithoutApple />
          </Box>
        )}
      </>
    );
  },
);

export const BodyWithSocialButtons: React.FC<{
  dividerText?: string;
}> = observer(({ children, dividerText }) => {
  const {
    rootStore: {
      displayStore: { authFormWidth, direction, isMobile, sideBySideForm },
    },
  } = useContext(AppContextProvider);
  return (
    <Box
      direction={direction}
      width="100%"
      maxWidth={authFormWidth}
      height={sideBySideForm.height('high')}
    >
      {isMobile ? (
        <>
          <Box marginBottom={4}>
            <SocialAuthWithoutApple width={sideBySideForm.width} />
          </Box>
          <AuthDivider text={dividerText} />
          {children}
        </>
      ) : (
        <>
          {children}
          <AuthDivider text={dividerText} className={s.horizontal} />
          <SocialAuthWithoutApple width={sideBySideForm.width} />
        </>
      )}
    </Box>
  );
});

export const BodyReverse: React.FC<{ dividerText?: string }> = observer(
  ({ children, dividerText }) => {
    const {
      rootStore: {
        displayStore: { authFormWidth },
      },
    } = useContext(AppContextProvider);
    return (
      <Box direction="vertical" width="100%" maxWidth={authFormWidth}>
        <SocialAuthIconsWithApple />
        <AuthDivider text={dividerText} />
        {children}
      </Box>
    );
  },
);

export const VerticalBody: React.FC<{
  socialFirst?: boolean;
  dividerText?: string;
  // Forwarded to SocialAuth: signup passes false to hide the Symphony "Scan QR
  // with the app" button (QR does login, so it's login-only per UX QA p3).
  showQrButton?: boolean;
}> = observer(
  ({ children, socialFirst = true, dividerText, showQrButton = true }) => {
    const {
      rootStore: {
        displayStore: { authFormWidth },
        socialAuthStore,
      },
    } = useContext(AppContextProvider);
    if (socialFirst) {
      return (
        <Box
          direction="vertical"
          align="center"
          width="100%"
          maxWidth={authFormWidth}
        >
          {!socialAuthStore.shouldHideSocialAuth && (
            <>
              <SocialAuth alignItems="center" showQrButton={showQrButton} />
              <AuthDivider text={dividerText} />
            </>
          )}
          {children}
        </Box>
      );
    }
    return (
      <Box direction="vertical" align="center" width="100%" maxWidth="320px">
        <Box
          direction="vertical"
          align="center"
          width="100%"
          maxWidth={authFormWidth}
        >
          {children}
          {!socialAuthStore.shouldHideSocialAuth && (
            <>
              <AuthDivider text={dividerText} />
              <SocialAuth alignItems="center" showQrButton={showQrButton} />
            </>
          )}
        </Box>
      </Box>
    );
  },
);

export const Policies: React.FC<{ className?: string }> = ({
  children,
  className,
}) => {
  return (
    <PoliciesContainer className={className}>{children}</PoliciesContainer>
  );
};

export const AuthDivider: React.FC<{ text?: string; className?: string }> = ({
  text,
  className,
}) => {
  const { t } = useTranslation();
  const {
    rootStore: {
      displayStore: { isVerticalLayout, preset },
    },
  } = useContext(AppContextProvider);

  const textStyle: React.CSSProperties | undefined = preset.dividerDesign
    ?.textColor
    ? { color: preset.dividerDesign.textColor }
    : undefined;

  const lineStyle: React.CSSProperties | undefined = preset.dividerDesign
    ?.lineColor
    ? { backgroundColor: preset.dividerDesign.lineColor }
    : undefined;

  return (
    <div
      className={classNames(s.dividerContainer, className, {
        [s.vertical]: isVerticalLayout,
      })}
    >
      <div className={s.line} style={lineStyle} />
      <ThemedText size="small" weight="thin">
        <div className={s.orText} style={textStyle}>
          {text ?? t(keys['emailStep.divider'])}
        </div>
      </ThemedText>
      <div className={s.line} style={lineStyle} />
    </div>
  );
};

export const BodyContainer: React.FC<{
  className?: string;
  width?: string;
  withPolicies?: boolean;
  priority?: 'primary' | 'secondary';
}> = ({ className, width, withPolicies, children, priority }) => {
  const {
    rootStore: {
      displayStore: {
        isVerticalLayout,
        preset,
        isMobile,
        isMobileFullScreen,
        extractBottomPadding,
      },
    },
  } = useContext(AppContextProvider);
  const containerWidth = useMemo(() => {
    if (width) {
      return width;
    }
    return isMobile ? '100%' : '536px';
  }, [isMobile, width]);

  const calculatedPriority = priority ?? 'primary';
  const calculateBottomPadding = useCallback(
    (padding) => {
      const currentBottomPadding = extractBottomPadding(padding);
      if (!currentBottomPadding || currentBottomPadding < 40) {
        return undefined;
      }
      const paddingToReduce = isMobile ? 30 : 40;
      const newBottomPadding = currentBottomPadding - paddingToReduce;
      return `${newBottomPadding}px`;
    },
    [extractBottomPadding, isMobile],
  );

  const containerStyle = isMobile
    ? preset.container?.mobile
    : preset.container?.desktop;
  const calculatedPadding =
    containerStyle && containerStyle[calculatedPriority].padding;
  const bottomPadding =
    withPolicies && containerStyle
      ? calculateBottomPadding(containerStyle[calculatedPriority].padding)
      : undefined;
  if (isVerticalLayout) {
    return (
      <Box
        direction="vertical"
        align="center"
        padding={calculatedPadding}
        paddingBottom={bottomPadding}
        backgroundColor="#fff"
        width={isMobileFullScreen ? '100%' : containerWidth}
        borderRadius={isMobileFullScreen ? '0' : containerStyle?.borderRadius}
        boxShadow={isMobileFullScreen ? 'none' : containerStyle?.boxShadow}
        className={classNames(s.bodyContainer, className)}
      >
        {children}
      </Box>
    );
  }
  return <>{children}</>;
};

export const AuthPage = Object.assign(_AuthPage, {
  Header,
  Body,
  BodyReverse,
  MobileBody,
  BodyWithSocialButtons,
  VerticalBody,
  Policies,
});
